import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Plus, MapPin, TruckIcon, Clock, Loader2 } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Badge } from '@/shared/components/ui/badge';
import { Progress } from '@/shared/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';
import { Link } from 'react-router-dom';
import { aiService } from '@/modules/ezparcel/services/aiService';
import { toast } from 'sonner';
import { LiveDispatchBoard } from '@/modules/ezparcel/components/dispatch/LiveDispatchBoard';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter } from
'@/shared/components/ui/dialog';
import { Label } from '@/shared/components/ui/label';
import { Input } from '@/shared/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue } from
'@/shared/components/ui/select';

const API_URL = API_BASE;

export const Routes = () => {const { t } = useTranslation("ezparcel");
  const [optimizing, setOptimizing] = useState(false);
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    zone: '',
    region: '',
    route_date: new Date().toISOString().split('T')[0],
    stops: []
  });

  const fetchRoutes = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('ezerpToken');
      const response = await fetch(`${API_URL}/api/ezparcel/dispatch-board`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setRoutes(data.routes || []);
      }
    } catch (err) {
      console.error('Failed to fetch routes:', err);
      toast.error('Failed to load routes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoutes();
  }, []);

  const handleCreateRoute = async (e) => {
    e.preventDefault();
    try {
      setIsSubmitting(true);
      const token = localStorage.getItem('ezerpToken');
      const response = await fetch(`${API_URL}/api/delivery/routes/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success('Route created successfully');
        setIsModalOpen(false);
        setFormData({
          zone: '',
          region: '',
          route_date: new Date().toISOString().split('T')[0],
          stops: []
        });
        fetchRoutes();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create route');
      }
    } catch (err) {
      console.error('Create route error:', err);
      toast.error('An error occurred while creating route');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOptimizeRoute = async (route) => {
    setOptimizing(true);
    toast.loading('AI is optimizing your route...');

    try {
      const result = await aiService.optimizeRoute(
        route.shipments?.map((id) => ({ trackingId: id, deliveryAddress: 'Sample Address' })) || [],
        { maxStops: route.totalStops, preferredTime: 'morning' }
      );

      toast.dismiss();
      toast.success('Route optimized successfully!', {
        description: result.substring(0, 100) + '...'
      });
    } catch (error) {
      toast.dismiss();
      toast.error('Optimization failed');
    } finally {
      setOptimizing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>);

  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t("routesDispatch", "Routes & Dispatch")}</h1>
          <p className="text-muted-foreground mt-2">{t("planOptimizeAnd", "Plan, optimize, and monitor delivery routes in real-time")}

          </p>
        </div>
        <Button
          onClick={() => setIsModalOpen(true)}
          className="bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent-hover))]">
          
          <Plus className="mr-2 h-4 w-4" />{t("createRoute", "Create Route")}

        </Button>
      </div>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{t("planNewDelivery", "Plan New Delivery Route")}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateRoute} className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="route_date">{t("routeDate", "Route Date")}</Label>
              <Input
                id="route_date"
                type="date"
                required
                value={formData.route_date}
                onChange={(e) => setFormData({ ...formData, route_date: e.target.value })} />
              
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="zone">{t("deliveryZone", "Delivery Zone")}</Label>
                <Select
                  value={formData.zone}
                  onValueChange={(val) => setFormData({ ...formData, zone: val })}>
                  
                  <SelectTrigger id="zone">
                    <SelectValue placeholder="Select Zone" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="metropolitan">{t("metropolitan", "Metropolitan")}</SelectItem>
                    <SelectItem value="suburban">{t("suburban", "Suburban")}</SelectItem>
                    <SelectItem value="regional">{t("regional", "Regional")}</SelectItem>
                    <SelectItem value="national">{t("national", "National")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="region">{t("region", "Region")}</Label>
                <Input
                  id="region"
                  placeholder="e.g. North Hub"
                  value={formData.region}
                  onChange={(e) => setFormData({ ...formData, region: e.target.value })} />
                
              </div>
            </div>

            <div className="bg-muted/50 p-4 rounded-lg border border-dashed border-border">
              <p className="text-xs text-center text-muted-foreground">{t("stopsCanBe", "Stops can be added after the route is created or automatically assigned by AI optimization.")}

              </p>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)}>{t("cancel", "Cancel")}

              </Button>
              <Button type="submit" disabled={isSubmitting} className="bg-blue-600 hover:bg-blue-700">
                {isSubmitting ?
                <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />{t("creating", "Creating...")}

                </> :

                'Create Route'
                }
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Tabs defaultValue="dispatch" className="space-y-4">
        <TabsList>
          <TabsTrigger value="dispatch">{t("liveDispatchBoard", "Live Dispatch Board")}</TabsTrigger>
          <TabsTrigger value="active">{t("activeRoutes", "Active Routes")}</TabsTrigger>
          <TabsTrigger value="planned">{t("planned", "Planned")}</TabsTrigger>
          <TabsTrigger value="completed">{t("completed", "Completed")}</TabsTrigger>
        </TabsList>

        <TabsContent value="dispatch">
          <LiveDispatchBoard />
        </TabsContent>

        <TabsContent value="active" className="space-y-4">
          {loading ?
          <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div> :
          routes.length === 0 ?
          <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <TruckIcon className="h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">{t("noActiveRoutes", "No Active Routes")}</h3>
                <p className="text-muted-foreground text-sm mb-4">{t("startADispatch", "Start a dispatch to create active routes")}

              </p>
              </CardContent>
            </Card> :

          <div className="grid gap-6 md:grid-cols-2">
              {routes.map((route) => {
              const progress = route.totalStops > 0 ? route.completedStops / route.totalStops * 100 : 0;

              return (
                <Card key={route.id} className="card-elevated">
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className="text-lg">{route.name || `Route ${route.id}`}</CardTitle>
                          <p className="text-sm text-muted-foreground mt-1">{route.id}</p>
                        </div>
                        <Badge className="status-badge status-in-progress">{t("active", "Active")}</Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-xs text-muted-foreground">{t("driver", "Driver")}</p>
                          <p className="font-medium text-sm mt-1">{route.driver || 'Unassigned'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">{t("vehicle", "Vehicle")}</p>
                          <p className="font-medium text-sm mt-1">{route.vehicle || 'Unassigned'}</p>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">{t("routeProgress", "Route Progress")}</span>
                          <span className="font-medium">
                            {route.completedStops || 0} / {route.totalStops || 0}{t("stops", "stops")}
                        </span>
                        </div>
                        <Progress value={progress} className="h-2" />
                      </div>

                      <div className="flex items-center gap-4 text-sm">
                        <span className="flex items-center gap-1.5 text-muted-foreground">
                          <MapPin className="h-4 w-4" />
                          {route.totalDistance || '0 km'}
                        </span>
                        <span className="flex items-center gap-1.5 text-muted-foreground">
                          <Clock className="h-4 w-4" />
                          {route.estimatedDuration || '0 min'}
                        </span>
                        <span className="flex items-center gap-1.5 text-muted-foreground">
                          <TruckIcon className="h-4 w-4" />
                          {route.shipments?.length || 0}{t("shipments", "shipments")}
                      </span>
                      </div>

                      {route.stops && route.stops.length > 0 &&
                    <div className="pt-2 border-t border-border">
                          <p className="text-xs font-medium text-muted-foreground mb-2">{t("recentStops", "Recent Stops")}</p>
                          <div className="space-y-2">
                            {route.stops.slice(0, 3).map((stop, idx) =>
                        <div key={idx} className="flex items-center justify-between text-sm">
                                <span className="text-foreground">{stop.address}</span>
                                <Badge
                            variant="outline"
                            className={stop.status === 'completed' ? 'border-green-500 text-green-700' : ''}>
                            
                                  {stop.status}
                                </Badge>
                              </div>
                        )}
                          </div>
                        </div>
                    }

                      <div className="flex gap-2">
                        <Link to={`/ezparcel/fleet/trips/${route.id}`} className="flex-1">
                          <Button variant="outline" className="w-full">
                            <MapPin className="mr-2 h-4 w-4" />{t("viewDetails", "View Details")}

                        </Button>
                        </Link>
                        <Button
                        variant="outline"
                        onClick={() => handleOptimizeRoute(route)}
                        disabled={optimizing}>{t("aiOptimize", "AI Optimize")}


                      </Button>
                      </div>
                    </CardContent>
                  </Card>);

            })}
            </div>
          }
        </TabsContent>

        <TabsContent value="planned">
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <TruckIcon className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">{t("noPlannedRoutes", "No Planned Routes")}</h3>
              <p className="text-muted-foreground text-sm mb-4">{t("createANew", "Create a new route to get started with planning")}

              </p>
              <Button
                onClick={() => setIsModalOpen(true)}
                className="bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent-hover))]">
                
                <Plus className="mr-2 h-4 w-4" />{t("createRoute", "Create Route")}

              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="completed">
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <TruckIcon className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">{t("noCompletedRoutes", "No Completed Routes Today")}</h3>
              <p className="text-muted-foreground text-sm">{t("completedRoutesWill", "Completed routes will appear here")}

              </p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>);

};